import React, { useState, useEffect } from "react";
import { X, Loader, ArrowRight, MessageSquare } from "lucide-react";
import api from "../../utils/api";
import { EXCEPTION_LABELS, SHEET_LABELS, fmtDate, fieldLabel } from "./ExcelUploadManager";

// Change history for one shipment record across accepted Excel uploads
const TYPE_STYLE = {
    new:        ["First seen",                "#10B981"],
    changed:    ["Changed",                   "#3B82F6"],
    reappeared: ["Back in report",            "#06B6D4"],
    missing:    ["Not in report",             "#F97316"],
};

const show = v => (v == null || v === "" ? "—" : Array.isArray(v) ? v.join(", ") : String(v));

// Line details shown when a row is selected — [field key, label, kind]; labels follow the report headers
const DETAIL_GROUPS = {
    pilot: [
        ["Material", [["sku","SKU"],["description","Description"],["hts","HTS #"],["hazardous_material","Hazardous"]]],
        ["Quantities", [["actual_quantity","Qty (units)","num"],["actual_packages","Cartons","num"],["actual_unit_quantity_per_package","Units / carton","num"],
            ["actual_measurement","CBM","num"],["actual_weight","Gross weight","num"],["actual_net_weight","Net weight","num"]]],
        ["Container", [["equipment_number","Container ID"],["equipment_size","Size"],["equipment_type","Type"],["equipment_seal","Seal"],
            ["feu","FEU","num"],["container_max_cbm","Container max CBM","num"],["origin_service","Origin service"],["destination_service","Dest. service"]]],
        ["Transport", [["carrier","Carrier"],["vessel","Vessel"],["voyage","Voyage"],["ship_mode","Ship mode"],["place_of_receipt_port","Origin"],
            ["discharge_port_city","Discharge port"],["destination_city","Destination"],["discharge_port_terminal_name","Terminal"]]],
        ["Dates", [["book_date","Book date","date"],["expected_receipt_erd","Expected Receipt (ERD)","date"],["actual_receipt_date","Actual receipt","date"],
            ["etd","ETD","date"],["eta","ETA","date"],["original_eta","Original ETA","date"],["last_vessel_arrival_actual","Vessel arrived","date"],
            ["gate_out_destination","Gate out destination","date"],["delivered","Delivered","date"]]],
        ["Order refs", [["po_number","PO"],["po_line","PO line"],["sales_order_number","Sales order"],["affiliate_po","Affiliate PO"],
            ["econnect_booking_no","eConnect booking"],["cbl_number","CBL"],["hbl_number","HBL"],["plant_code","Plant"],
            ["supplier","Supplier"],["shipper","Shipper"],["consignee_entity","Consignee"]]],
    ],
    outbound: [
        ["Delivery", [["receiver_name","Receiver"],["suburb","Suburb"],["receiving_state","State"],["delivery_site","Delivery site"],["purchase_order_number","PO"]]],
        ["Quantities", [["carton_count","Cartons","num"],["pallet_estimation","Pallets (est.)","num"]]],
        ["Dates", [["earliest_pick_up_date","Earliest pick-up","date"],["required_delivery_date","Required delivery","date"],
            ["requested_dispatch_date","Requested dispatch","date"],["booking_date","Booking date","date"],["booking_time","Booking time"],["delivered_date","Delivered","date"]]],
        ["Notes", [["required_delivery_text","Required delivery (text)"],["booking_note","Booking note"],["requested_dispatch_note","Dispatch note"],
            ["original_connotes","Original connotes"],["comments","Comments"]]],
    ],
};

function fmtDetail(v, kind) {
    if (v == null || v === "") return "—";
    if (kind === "num" && typeof v === "number") return v.toLocaleString("en-AU", { maximumFractionDigits: 3 });
    if (kind === "date" && typeof v === "string") {
        const base = fmtDate(v.slice(0, 10));
        return v.length > 10 ? `${base} ${v.slice(11, 16)}` : base;
    }
    return show(v);
}

function LineDetails({ shipment }) {
    const groups = DETAIL_GROUPS[shipment.sheetCode === "pilot" ? "pilot" : "outbound"];
    const d = shipment.data || {};
    return (
        <div className="mb-5 space-y-3">
            {groups.map(([title, fields]) => (
                <div key={title} className="bg-[#0D1520] border border-[#1F2937] rounded-lg px-3 py-2.5">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">{title}</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                        {fields.map(([key, label, kind]) => (
                            <React.Fragment key={key}>
                                <span className="text-slate-500">{label}</span>
                                <span className={`text-slate-200 break-words ${key === "equipment_number" ? "font-mono" : ""}`}>
                                    {key === "equipment_number" && !d[key]
                                        ? <span className="text-slate-600 italic">Not assigned</span>
                                        : fmtDetail(d[key], kind)}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

// User comments on this line — stored apart from the imported data, so uploads never change them
function Comments({ shipmentId, initial, onAdded }) {
    const [list, setList]       = useState(initial || []);
    const [text, setText]       = useState("");
    const [posting, setPosting] = useState(false);
    const [err, setErr]         = useState(null);

    const add = async () => {
        const clean = text.trim();
        if (!clean) return;
        setPosting(true); setErr(null);
        try {
            const res = await api.post(`/api/excel-ingestion/shipments/${encodeURIComponent(shipmentId)}/comments`, { text: clean });
            setList(l => [res.data, ...l]);
            setText("");
            onAdded?.();
        } catch (e) {
            setErr(e.response?.data?.error || "Could not save the comment");
        } finally {
            setPosting(false);
        }
    };

    return (
        <div className="mb-5 bg-[#0D1520] border border-[#1F2937] rounded-lg px-3 py-2.5">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <MessageSquare size={11}/> Comments {list.length > 0 && <span className="text-slate-600">({list.length})</span>}
            </p>
            <textarea value={text} onChange={e => setText(e.target.value)} maxLength={1000} rows={2}
                placeholder="Add a comment for the team…"
                onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) add(); }}
                className="w-full bg-[#111827] border border-[#1F2937] rounded-md px-2 py-1.5 text-[12px] text-slate-200 placeholder:text-slate-600 resize-y focus:border-blue-500/50 outline-none"/>
            <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-slate-600">{text.length}/1000 · ⌘/Ctrl+Enter to save</span>
                <button onClick={add} disabled={posting || !text.trim()}
                    className="px-3 py-1 rounded-md bg-blue-600 text-[11px] font-semibold text-white hover:bg-blue-500 disabled:opacity-40">
                    {posting ? "Saving…" : "Add comment"}
                </button>
            </div>
            {err && <p className="text-[11px] text-red-400 mt-1">{err}</p>}
            {list.length > 0 && (
                <ul className="mt-2 space-y-2 border-t border-[#1F2937] pt-2">
                    {list.map(c => (
                        <li key={c.id} className="text-[11px]">
                            <p className="text-slate-200 whitespace-pre-wrap break-words">{c.text}</p>
                            <p className="text-[10px] text-slate-600">{c.by || "—"} · {c.at ? new Date(c.at).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function ExcelShipmentHistory({ shipmentId, onClose, onCommentAdded }) {
    const [data, setData]   = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get(`/api/excel-ingestion/shipments/${encodeURIComponent(shipmentId)}/history`)
            .then(res => alive && setData(res.data))
            .catch(e => alive && setError(e.response?.data?.error || "Could not load history"));
        return () => { alive = false; };
    }, [shipmentId]);

    const labels = data?.statusLabels || {};
    const s = data?.shipment;
    const label = c => (c ? labels[c] || c : "—");

    return (
        <div className="fixed inset-0 flex justify-end" style={{ background: "rgba(0,0,0,0.6)", zIndex: 9999 }} onClick={onClose}>
            <div className="bg-[#111827] border-l border-[#1F2937] w-full max-w-lg h-full overflow-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between px-5 py-4 border-b border-[#1F2937] sticky top-0 bg-[#111827]">
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-white font-mono truncate">{s?.ref || "Shipment history"}</p>
                        {s && (
                            <p className="text-[11px] text-slate-500 mt-0.5">
                                {SHEET_LABELS[s.sheetCode] || s.sheetCode} · {s.party || "—"} · {label(s.status)}
                                {s.active === false && <span className="text-orange-400"> · not in latest report since {fmtDate(s.missingSince)}</span>}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-lg bg-[#1F2937] flex items-center justify-center text-slate-400 hover:text-white shrink-0">
                        <X size={15}/>
                    </button>
                </div>

                <div className="p-5">
                    {error && <p className="text-[12px] text-red-400">{error}</p>}
                    {!data && !error && <Loader size={18} className="animate-spin text-slate-600 mx-auto mt-10"/>}

                    {s && (
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-5 text-[11px]">
                            <span className="text-slate-500">ETA / required delivery</span><span className="text-slate-200">{fmtDate(s.eta)}</span>
                            <span className="text-slate-500">Status (as written)</span><span className="text-slate-200">{show(s.statusRaw)}</span>
                            <span className="text-slate-500">First seen</span><span className="text-slate-200">{fmtDate(s.firstSeenReportDate)}</span>
                            <span className="text-slate-500">Last changed</span><span className="text-slate-200">{fmtDate(s.lastChangedReportDate)}</span>
                            {s.exceptions?.length > 0 && (<>
                                <span className="text-slate-500">Exceptions</span>
                                <span className="text-red-400">{s.exceptions.map(e => EXCEPTION_LABELS[e] || e).join(", ")}</span>
                            </>)}
                        </div>
                    )}

                    {s && <Comments shipmentId={shipmentId} initial={data.comments} onAdded={onCommentAdded}/>}
                    {s && <LineDetails shipment={s}/>}
                    {data && <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Change history</p>}

                    {data && (
                        <ol className="relative border-l border-[#1F2937] ml-1.5 space-y-4">
                            {data.history.length === 0 && <p className="text-[12px] text-slate-500 pl-4">No history yet.</p>}
                            {data.history.map(h => {
                                const [title, color] = TYPE_STYLE[h.type] || [h.type, "#6B7280"];
                                return (
                                    <li key={h.uploadId} className="pl-4">
                                        <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full mt-1" style={{ backgroundColor: color }}/>
                                        <p className="text-[12px] font-semibold" style={{ color }}>
                                            {title} <span className="text-slate-500 font-normal">· report {fmtDate(h.reportDate)}</span>
                                        </p>
                                        <p className="text-[10px] text-slate-600 truncate">{h.fileName}</p>
                                        <div className="mt-1 space-y-0.5 text-[11px] text-slate-300">
                                            {h.statusChange && (
                                                <p>Status: <span className="text-slate-500">{label(h.statusChange.from)}</span>
                                                    <ArrowRight size={10} className="inline mx-1"/>{label(h.statusChange.to)}</p>
                                            )}
                                            {h.etaChange && (
                                                <p>ETA: <span className="text-slate-500">{fmtDate(h.etaChange.from)}</span>
                                                    <ArrowRight size={10} className="inline mx-1"/>{fmtDate(h.etaChange.to)}
                                                    {h.etaChange.days ? <span className={h.etaChange.days > 0 ? "text-amber-400" : "text-green-400"}> ({h.etaChange.days > 0 ? "+" : ""}{h.etaChange.days}d)</span> : null}
                                                </p>
                                            )}
                                            {h.type === "new" && <p className="text-slate-500">Status: {label(h.status)} · ETA {fmtDate(h.eta)}</p>}
                                            {(h.changes || []).filter(c => !(h.statusChange && c.field === "comments") && !(h.etaChange && ["eta", "required_delivery_date"].includes(c.field))).slice(0, 12).map(c => (
                                                <p key={c.field} className="text-slate-500">
                                                    {fieldLabel(c.field)}: {show(c.from)} <ArrowRight size={10} className="inline mx-0.5"/> <span className="text-slate-300">{show(c.to)}</span>
                                                </p>
                                            ))}
                                            {h.addedExceptions?.length > 0 && (
                                                <p className="text-red-400">New exception: {h.addedExceptions.map(e => EXCEPTION_LABELS[e] || e).join(", ")}</p>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
            </div>
        </div>
    );
}
